// ============================================================
// FormInputs.jsx — Shared form input components for DPP React
//
// NO INLINE STYLES. Every visual property lives in index.css under
// the "FORM INPUTS — NO INLINE STYLES" section. The only exceptions
// are in ColorInput, where `background` and `color` are literally
// the arbitrary hex value the user picked — that's user data, not a
// design decision, so it cannot be a static class. Everything else
// — including the wheel picker engine's geometry, which used to be
// computed inline — is now real CSS classes plus a small set of
// size classes for the handful of truly per-instance values (wheel
// column width) instead of raw inline width.
//
// Exports:
//   - DateInput        : mobile = wheel picker (month/day/year)
//                        desktop = typed text field + branded calendar popover
//   - TimeInput        : mobile = wheel picker (hour/minute/AM-PM)
//                        desktop = typed text field + branded time-list popover
//   - TimezoneInput    : curated US select with custom arrow (unchanged)
//   - ColorInput       : Coloris-powered color picker (unchanged)
//
// Also exports:
//   - TIMEZONES
//   - useTimezoneAutoDetect
//   - formatTimeAMPM   : 24h "HH:MM" storage string -> 12h "h:MM AM/PM"
//                        display string, for read-only time display
//                        anywhere in the app (dashboard, gig cards,
//                        gig details, calendar, etc). Shares the same
//                        parseHHMM/to12h helpers TimeInput itself uses,
//                        so display formatting can never drift out of
//                        sync with the input's own 12h/24h conversion.
//
// Storage contract is unchanged:
//   - DateInput emits { target: { value: "YYYY-MM-DD", name } }
//   - TimeInput emits { target: { value: "HH:MM", name } } in 24h format
// ============================================================

import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

// ─────────────────────────────────────────────────────────────
// TIMEZONES
// ─────────────────────────────────────────────────────────────
export const TIMEZONES = [
  { value: 'America/New_York',    label: 'ET  - Eastern Time' },
  { value: 'America/Chicago',     label: 'CT  - Central Time' },
  { value: 'America/Denver',      label: 'MT  - Mountain Time' },
  { value: 'America/Phoenix',     label: 'MT  - Arizona (no DST)' },
  { value: 'America/Los_Angeles', label: 'PT  - Pacific Time' },
  { value: 'America/Anchorage',   label: 'AKT - Alaska Time' },
  { value: 'Pacific/Honolulu',    label: 'HT  - Hawaii Time' },
  { value: 'UTC',                 label: 'UTC - Coordinated Universal Time' },
];

// ─────────────────────────────────────────────────────────────
// useTimezoneAutoDetect
// ─────────────────────────────────────────────────────────────
export function useTimezoneAutoDetect({ lat, lon, onDetected }) {
  const [detecting, setDetecting]   = useState(false);
  const [detectedTz, setDetectedTz] = useState(null);
  const lastFetched   = useRef(null);
  const onDetectedRef = useRef(onDetected);
  useEffect(() => { onDetectedRef.current = onDetected; });

  useEffect(() => {
    if (!lat || !lon) return;
    const key = `${lat},${lon}`;
    if (lastFetched.current === key) return;
    lastFetched.current = key;
    setDetecting(true);
    fetch(
      `https://api.bigdatacloud.net/data/timezone-by-location?latitude=${lat}&longitude=${lon}&localityLanguage=en`
    )
      .then(res => res.json())
      .then(data => {
        const tz = data.ianaTimeId;
        if (tz) { setDetectedTz(tz); onDetectedRef.current(tz); }
      })
      .catch(() => {})
      .finally(() => setDetecting(false));
  }, [lat, lon]);

  return { detecting, detectedTz };
}

// ─────────────────────────────────────────────────────────────
// SHARED LABEL HELPER
// ─────────────────────────────────────────────────────────────
function FieldLabel({ htmlFor, label, required }) {
  if (!label) return null;
  return (
    <label htmlFor={htmlFor} className="fi-label">
      {label}
      {required && <span className="fi-label-required" aria-hidden="true"> *</span>}
    </label>
  );
}

// ─────────────────────────────────────────────────────────────
// SHARED CLICK-OUTSIDE HOOK
// ─────────────────────────────────────────────────────────────
function useClickOutside(refs, onOutside) {
  useEffect(() => {
    const refList = Array.isArray(refs) ? refs : [refs];
    function handler(e) {
      const isInside = refList.some(r => r.current && r.current.contains(e.target));
      if (!isInside) onOutside();
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [refs, onOutside]);
}

// ═════════════════════════════════════════════════════════════
// WHEEL PICKER ENGINE (mobile date/time entry)
// ═════════════════════════════════════════════════════════════
//
// Geometry is fixed and lives in CSS (.fi-wheel-col, .fi-wheel-band,
// .fi-wheel-track, .fi-wheel-item) rather than being computed inline.
// ITEM_HEIGHT/VISIBLE below MUST stay in sync with the matching
// values in index.css since the scroll-position math depends on
// them — see the CSS comment there.
// ─────────────────────────────────────────────────────────────

const ITEM_HEIGHT = 40;          // px per row — must match CSS
// VISIBLE (5 rows shown at once) is no longer computed here — its
// value is hardcoded directly in the CSS geometry (.fi-wheel-col
// height: 200px = 5 * 40px), same as PAD was. If the row count or
// ITEM_HEIGHT ever changes, update index.css by hand too.
// PAD (top/bottom padding) is no longer computed here — its value
// (80px = ((5-1)/2) * 40px) is hardcoded directly in the CSS
// (.fi-wheel-band / .fi-wheel-track), since the geometry moved out
// of inline styles. Keep ITEM_HEIGHT/VISIBLE in sync with CSS; if
// either changes, recompute 80px by hand in index.css too.

// Discrete width classes replace the old raw `width` prop. Add a new
// one here + in CSS (.fi-wheel-col--yourname) if a new size is ever needed.
const WHEEL_SIZE_CLASS = {
  hourmin: 'fi-wheel-col--hourmin',  // 3rem   — hour & minute wheels
  month:   'fi-wheel-col--month',    // 3.75rem
  day:     'fi-wheel-col--day',      // 2.75rem
  year:    'fi-wheel-col--year',     // 4rem
};

function WheelColumn({ items, value, onChange, ariaLabel, size = 'hourmin', id, onActivate }) {
  const scrollRef   = useRef(null);
  const isSyncing   = useRef(false);
  const settleTimer = useRef(null);
  const tapStart    = useRef(null);

  const selectedIndex = Math.max(0, items.findIndex(it => it.value === value));
  const [liveIndex, setLiveIndex] = useState(selectedIndex);

  useEffect(() => { setLiveIndex(selectedIndex); }, [selectedIndex]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    isSyncing.current = true;
    el.scrollTop = selectedIndex * ITEM_HEIGHT;
    const t = setTimeout(() => { isSyncing.current = false; }, 60);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, items.length]);

  function onSettle() {
    const el = scrollRef.current;
    if (!el) return;
    const idx = Math.max(0, Math.min(items.length - 1, Math.round(el.scrollTop / ITEM_HEIGHT)));
    if (isSyncing.current) { isSyncing.current = false; return; }
    const next = items[idx];
    if (next && next.value !== value) onChange(next.value);
  }

  function onScroll() {
    const el = scrollRef.current;
    if (el) {
      const idx = Math.max(0, Math.min(items.length - 1, Math.round(el.scrollTop / ITEM_HEIGHT)));
      if (idx !== liveIndex) setLiveIndex(idx);
    }
    if (settleTimer.current) clearTimeout(settleTimer.current);
    settleTimer.current = setTimeout(onSettle, 110);
  }

  function userScrollTo(idx) {
    const el = scrollRef.current;
    if (!el) return;
    const clamped = Math.max(0, Math.min(items.length - 1, idx));
    el.scrollTo({ top: clamped * ITEM_HEIGHT, behavior: 'smooth' });
  }

  function onKeyDown(e) {
    if (e.key === 'ArrowUp')   { e.preventDefault(); userScrollTo(selectedIndex - 1); }
    if (e.key === 'ArrowDown') { e.preventDefault(); userScrollTo(selectedIndex + 1); }
  }

  function onPointerDown(e) {
    tapStart.current = { y: e.clientY ?? 0, t: Date.now() };
  }
  function onPointerUp(e) {
    const s = tapStart.current;
    tapStart.current = null;
    if (!s || typeof onActivate !== 'function') return;
    const movedY = Math.abs((e.clientY ?? 0) - s.y);
    if (movedY < 8 && Date.now() - s.t < 400) onActivate();
  }

  const sizeClass = WHEEL_SIZE_CLASS[size] || WHEEL_SIZE_CLASS.hourmin;

  return (
    <div className={`fi-wheel-col ${sizeClass}`}>
      <div className="fi-wheel-band" aria-hidden="true" />
      <div
        ref={scrollRef}
        id={id}
        className="fi-wheel-track wheel-col"
        role="listbox"
        aria-label={ariaLabel}
        tabIndex={0}
        onScroll={onScroll}
        onKeyDown={onKeyDown}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
      >
        {items.map((it, i) => {
          const active = i === liveIndex;
          return (
            <div
              key={it.value}
              role="option"
              aria-selected={active}
              onClick={() => { if (typeof onActivate !== 'function') userScrollTo(i); }}
              className={`fi-wheel-item${active ? ' fi-wheel-item--active' : ''}`}
            >
              {it.label}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function useCoarsePointer() {
  const query = '(pointer: coarse)';
  const [coarse, setCoarse] = useState(
    () => typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia(query).matches
      : false
  );
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia(query);
    const onChange = (e) => setCoarse(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  return coarse;
}

// ─────────────────────────────────────────────────────────────
// TIME UTILITIES (24h storage <-> 12h display)
// ─────────────────────────────────────────────────────────────

function parseHHMM(str) {
  if (!str || typeof str !== 'string') return null;
  const parts = str.split(':');
  if (parts.length < 2) return null;
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (isNaN(h) || isNaN(m)) return null;
  return { hours: h, minutes: m };
}

function toHHMM(h24, m) {
  return `${String(h24).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function to12h(h24) {
  const h = h24 % 12;
  return h === 0 ? 12 : h;
}

function to24h(h12, period) {
  const h = parseInt(h12, 10);
  if (isNaN(h)) return 0;
  if (period === 'AM') return h === 12 ? 0 : h;
  return h === 12 ? 12 : h + 12;
}

// ── formatTimeAMPM ────────────────────────────────────────────
// Converts a 24-hour "HH:MM" storage string into a 12-hour
// "h:MM AM/PM" display string, for use anywhere the app shows a
// gig time, call time, or pickup time to the person. Reuses the
// same parseHHMM/to12h helpers TimeInput already relies on, so
// display formatting and the wheel/typed-entry logic can never
// drift out of sync with each other.
//
// Returns an empty string for empty/undefined input, and returns
// the original value unchanged if it doesn't parse as "HH:MM" --
// safe to call on anything without needing a null-check first at
// each call site.
//
// Example: formatTimeAMPM("18:05") -> "6:05 PM"
//          formatTimeAMPM("00:00") -> "12:00 AM"
//          formatTimeAMPM("")      -> ""
export function formatTimeAMPM(value) {
  if (!value) return '';
  const parsed = parseHHMM(value);
  if (!parsed) return value;
  // not a recognizable 24h "HH:MM" string -- show it as-is rather
  // than silently hiding a value the caller expected to see

  const hour12 = to12h(parsed.hours);
  const minute = String(parsed.minutes).padStart(2, '0');
  const period = parsed.hours >= 12 ? 'PM' : 'AM';
  return `${hour12}:${minute} ${period}`;
}

const HOUR_ITEMS   = Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }));
const MINUTE_ITEMS = Array.from({ length: 60 }, (_, i) => ({ value: String(i), label: String(i).padStart(2, '0') }));

// Clamp a raw hour string to the 12h range (1-12) for typed entry.
function clampHourVal(raw) {
  const n = parseInt(raw, 10);
  if (isNaN(n) || n < 1) return '1';
  if (n > 12) return '12';
  return String(n);
}

// Clamp a raw minute string to 0-59 (unpadded, matches wheel item values).
function clampMinuteVal(raw) {
  const n = parseInt(raw, 10);
  if (isNaN(n) || n < 0) return '0';
  if (n > 59) return '59';
  return String(n);
}

// ─────────────────────────────────────────────────────────────
// TimeInput
//
// Mobile (coarse pointer): wheel/dial picker for hour + minute,
//   plus AM/PM toggle buttons. Unchanged.
// Desktop (fine pointer): standard typed entry — separate hour and
//   minute number fields, plus a native AM/PM <select>. No free-text
//   parsing and no popover list; this is the plain, familiar pattern.
// ─────────────────────────────────────────────────────────────
export function TimeInput({ label = '', value = '', onChange, required = false, hint, id, name }) {
  const inputId = id || name || (label ? `time-input-${label.replace(/\s+/g, '-').toLowerCase()}` : 'time-input');

  const isTouch = useCoarsePointer();
  const parsed  = parseHHMM(value);

  const [hour,   setHour]   = useState(parsed ? String(to12h(parsed.hours)) : '12');
  const [minute, setMinute] = useState(parsed ? String(parsed.minutes)      : '0');
  const [period, setPeriod] = useState(parsed ? (parsed.hours >= 12 ? 'PM' : 'AM') : 'PM');

  // Desktop minute field shows raw digits while focused ("5"), and
  // padded ("05") once blurred, matching a standard time input feel.
  const [minFocused, setMinFocused] = useState(false);

  useEffect(() => {
    const p = parseHHMM(value);
    if (p) {
      setHour(String(to12h(p.hours)));
      setMinute(String(p.minutes));
      setPeriod(p.hours >= 12 ? 'PM' : 'AM');
    } else {
      setHour('12');
      setMinute('0');
      setPeriod('PM');
    }
  }, [value]);

  function emit(h, m, p) {
    if (h === '' || h == null || m === '' || m == null) return;
    const h24 = to24h(h, p);
    const mi  = parseInt(m, 10);
    if (isNaN(h24) || isNaN(mi)) return;
    if (typeof onChange === 'function') {
      onChange({ target: { value: toHHMM(h24, mi), name } });
    }
  }
  function changeHour(v)   { setHour(v);   emit(v, minute, period); }
  function changeMinute(v) { setMinute(v); emit(hour, v, period); }
  function changePeriod(v) { setPeriod(v); emit(hour, minute, v); }

  // ── Desktop typed hour field ────────────────────────────────
  function handleHourType(e) {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 2);
    changeHour(raw);
  }
  function handleHourBlur() {
    if (hour === '') return;
    changeHour(clampHourVal(hour));
  }
  // Jump to the minute field once the hour is unambiguous.
  function handleHourKeyUp(e) {
    const n = parseInt(e.target.value, 10);
    if (!isNaN(n) && (e.target.value.length === 2 || n >= 2)) {
      document.getElementById(`${inputId}-min`)?.focus();
    }
  }

  // ── Desktop typed minute field ──────────────────────────────
  function handleMinuteType(e) {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 2);
    changeMinute(raw);
  }
  function handleMinuteBlur() {
    setMinFocused(false);
    if (minute === '') return;
    changeMinute(clampMinuteVal(minute));
  }
  const minuteDisplay = minFocused ? minute : (minute === '' ? '' : minute.padStart(2, '0'));

  return (
    <div className="form-group">
      <FieldLabel htmlFor={inputId} label={label} required={required} />

      {isTouch ? (
        // ── Mobile: dial / wheel picker + AM/PM toggle buttons ──
        <div className="fi-wheel-frame" role="group" aria-label={label || 'Time'}>
          <WheelColumn id={inputId} items={HOUR_ITEMS} value={hour} onChange={changeHour} ariaLabel="Hour" size="hourmin" />
          <span className="fi-wheel-colon" aria-hidden="true">:</span>
          <WheelColumn items={MINUTE_ITEMS} value={minute} onChange={changeMinute} ariaLabel="Minute" size="hourmin" />
          <div className="fi-ampm-group" role="group" aria-label="AM or PM">
            {['AM', 'PM'].map(p => (
              <button
                type="button"
                key={p}
                className={`fi-period-btn${period === p ? ' fi-period-btn--active' : ''}`}
                onClick={() => changePeriod(p)}
                aria-pressed={period === p}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      ) : (
        // ── Desktop: typed hour + minute, AM/PM select ──
        <div className="fi-time-desktop" role="group" aria-label={label || 'Time'}>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            id={inputId}
            name={name}
            className="fi-time-input"
            value={hour}
            onChange={handleHourType}
            onKeyUp={handleHourKeyUp}
            onBlur={handleHourBlur}
            onFocus={(e) => e.target.select()}
            placeholder="12"
            aria-label="Hour"
            required={required}
          />
          <span className="fi-wheel-colon" aria-hidden="true">:</span>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            id={`${inputId}-min`}
            className="fi-time-input"
            value={minuteDisplay}
            onChange={handleMinuteType}
            onFocus={(e) => { setMinFocused(true); e.target.select(); }}
            onBlur={handleMinuteBlur}
            placeholder="00"
            aria-label="Minute"
          />
          <select
            className="fi-time-ampm-select"
            value={period}
            onChange={(e) => changePeriod(e.target.value)}
            aria-label="AM or PM"
          >
            <option value="AM">AM</option>
            <option value="PM">PM</option>
          </select>
        </div>
      )}

      {value && (
        <button type="button" className="fi-clear-btn" onClick={() => onChange({ target: { value: '', name } })}>
          Clear
        </button>
      )}

      {hint && <small className="fi-hint">{hint}</small>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// DATE UTILITIES
// ─────────────────────────────────────────────────────────────
const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

const WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function pad2(n) { return String(n).padStart(2, '0'); }

function parseYMD(str) {
  if (!str || typeof str !== 'string') return null;
  const parts = str.split('-');
  if (parts.length < 3) return null;
  const [y, m, d] = parts.map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function daysInMonth(year, monthIdx) {
  return new Date(year, monthIdx + 1, 0).getDate();
}

function parseTypedDate(raw) {
  if (!raw || typeof raw !== 'string') return null;
  const trimmed = raw.trim();

  let m = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) return validateYMD(+m[1], +m[2], +m[3]);

  m = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (m) {
    let year = +m[3];
    if (year < 100) year += 2000;
    return validateYMD(year, +m[1], +m[2]);
  }

  const fallback = new Date(trimmed);
  if (!isNaN(fallback.getTime())) {
    return validateYMD(fallback.getFullYear(), fallback.getMonth() + 1, fallback.getDate());
  }
  return null;
}

function validateYMD(y, m, d) {
  if (m < 1 || m > 12) return null;
  if (d < 1 || d > daysInMonth(y, m - 1)) return null;
  return { y, m, d };
}

function formatDisplayDate(value) {
  const parsed = parseYMD(value);
  if (!parsed) return '';
  return `${MONTH_NAMES[parsed.getMonth()].slice(0, 3)} ${parsed.getDate()}, ${parsed.getFullYear()}`;
}

function buildMonthGrid(year, monthIdx) {
  const firstOfMonth = new Date(year, monthIdx, 1);
  const startWeekday = firstOfMonth.getDay();
  const gridStart = new Date(year, monthIdx, 1 - startWeekday);
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    cells.push(d);
  }
  return cells;
}

// ─────────────────────────────────────────────────────────────
// DateInput — calendar date picker, used on all devices (no
// separate mobile wheel variant).
//
// Props:
//   label     string
//   value     string  YYYY-MM-DD
//   onChange  fn      receives { target: { value: "YYYY-MM-DD", name } }
//   required  bool
//   hint      string  optional helper text shown below
//   id/name   string
//   minYear   number  optional, lowest navigable year (default: this year - 1)
//   maxYear   number  optional, highest navigable year (default: this year + 5)
// ─────────────────────────────────────────────────────────────
export function DateInput({ label = 'Date', value = '', onChange, required = false, hint, id, name, minYear, maxYear }) {
  const inputId = id || name || `date-input-${(label || 'date').replace(/\s+/g, '-').toLowerCase()}`;

  const today  = new Date();
  const loYear = minYear ?? today.getFullYear() - 1;
  const hiYear = maxYear ?? today.getFullYear() + 5;
  const isTouch = useCoarsePointer();

  const [typedText, setTypedText] = useState(formatDisplayDate(value));
  const [open, setOpen]           = useState(false);
  const [viewYear, setViewYear]   = useState(() => {
    const p = parseYMD(value);
    return Math.min(Math.max((p || today).getFullYear(), loYear), hiYear);
  });
  const [viewMonth, setViewMonth] = useState(() => (parseYMD(value) || today).getMonth());
  const fieldRef = useRef(null);
  const sheetRef = useRef(null);
  useClickOutside([fieldRef, sheetRef], () => setOpen(false));

  useEffect(() => { setTypedText(formatDisplayDate(value)); }, [value]);

  useEffect(() => {
    if (!open) return;
    function onKeyDown(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  function commitTyped() {
    const result = parseTypedDate(typedText);
    if (result && typeof onChange === 'function') {
      onChange({ target: { value: `${result.y}-${pad2(result.m)}-${pad2(result.d)}`, name } });
    } else {
      setTypedText(formatDisplayDate(value));
    }
  }

  function openCalendar() {
    const p = parseYMD(value) || today;
    setViewYear(Math.min(Math.max(p.getFullYear(), loYear), hiYear));
    setViewMonth(p.getMonth());
    setOpen(true);
  }

  function goMonth(delta) {
    let m = viewMonth + delta, y = viewYear;
    if (m < 0) { m = 11; y -= 1; }
    if (m > 11) { m = 0; y += 1; }
    if (y < loYear || y > hiYear) return; // stay within the navigable range
    setViewMonth(m); setViewYear(y);
  }

  function selectDay(d) {
    if (typeof onChange === 'function') {
      onChange({ target: { value: `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`, name } });
    }
    setOpen(false);
  }

  const gridCells   = buildMonthGrid(viewYear, viewMonth);
  const selectedISO = value || null;
  const todayISO    = `${today.getFullYear()}-${pad2(today.getMonth() + 1)}-${pad2(today.getDate())}`;

  return (
    <div className="form-group">
      <FieldLabel htmlFor={inputId} label={label} required={required} />

      <div className="dt-field" ref={fieldRef}>
        <input
          type="text"
          id={inputId}
          name={name}
          className="dt-field-input"
          value={typedText}
          placeholder="MM/DD/YYYY"
          onChange={(e) => setTypedText(e.target.value)}
          onBlur={commitTyped}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.target.blur();
            if (e.key === 'Escape') setOpen(false);
          }}
          onFocus={(e) => e.target.select()}
          required={required}
        />
        <button
          type="button"
          className="dt-icon-btn"
          aria-label="Open calendar"
          onClick={() => (open ? setOpen(false) : openCalendar())}
        >
          <i className="bi bi-calendar3"></i>
        </button>
        {open && (() => {
          const calendarContent = (
            <div className="dt-cal-header">
              <button type="button" className="dt-cal-nav-btn" onClick={() => goMonth(-1)} aria-label="Previous month" disabled={viewYear <= loYear && viewMonth === 0}>
                <i className="bi bi-chevron-left"></i>
              </button>
              <span className="dt-cal-title">{MONTH_NAMES[viewMonth]} {viewYear}</span>
              <button type="button" className="dt-cal-nav-btn" onClick={() => goMonth(1)} aria-label="Next month" disabled={viewYear >= hiYear && viewMonth === 11}>
                <i className="bi bi-chevron-right"></i>
              </button>
            </div>
          );

          const dayGrid = (
            <div className="dt-cal-grid">
              {WEEKDAY_LABELS.map(w => <div key={w} className="dt-cal-weekday">{w}</div>)}
              {gridCells.map((d, i) => {
                const iso     = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
                const inMon   = d.getMonth() === viewMonth;
                const isSel   = iso === selectedISO;
                const isToday = iso === todayISO;
                const cls = [
                  'dt-cal-day',
                  !inMon && 'dt-cal-day--muted',
                  isToday && !isSel && 'dt-cal-day--today',
                  isSel && 'dt-cal-day--selected',
                ].filter(Boolean).join(' ');
                return (
                  <button type="button" key={i} className={cls} onClick={() => selectDay(d)}>
                    {d.getDate()}
                  </button>
                );
              })}
            </div>
          );

          const footer = (
            <div className="dt-cal-footer">
              <button type="button" className="dt-cal-today-btn" onClick={() => selectDay(today)}>
                Today
              </button>
            </div>
          );

          if (isTouch) {
            // Portaled straight to document.body so this sheet is no
            // longer a DOM descendant of whatever modal it opened
            // inside (e.g. AddTripModal's .atm-overlay, which has
            // backdrop-filter). backdrop-filter creates a new
            // containing block for position:fixed descendants, so
            // without the portal this sheet centers itself against
            // the modal's box instead of the real viewport.
            return createPortal(
              <>
                <div className="dt-popover--sheet-backdrop" onClick={() => setOpen(false)} />
                <div ref={sheetRef} className="dt-popover dt-cal dt-popover--sheet">
                  <button
                    type="button"
                    className="dt-popover-sheet-close"
                    aria-label="Close calendar"
                    onClick={() => setOpen(false)}
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                  {calendarContent}
                  {dayGrid}
                  {footer}
                </div>
              </>,
              document.body
            );
          }

          // Desktop: small anchored dropdown, rendered in place as before.
          return (
            <div className="dt-popover dt-cal">
              {calendarContent}
              {dayGrid}
              {footer}
            </div>
          );
        })()}
      </div>

      {value && (
        <button type="button" className="fi-clear-btn" onClick={() => onChange({ target: { value: '', name } })}>
          Clear
        </button>
      )}

      {hint && <small className="fi-hint">{hint}</small>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// TimezoneInput
// ─────────────────────────────────────────────────────────────
export function TimezoneInput({
  label = 'Timezone',
  value,
  onChange,
  required       = false,
  autoDetectNote = null,
  detecting      = false,
  id,
  name,
  ...rest
}) {
  const selectId  = id || name || (label ? `tz-input-${label.replace(/\s+/g, '-').toLowerCase()}` : 'tz-input');
  const safeValue = value || '';
  const isInList  = TIMEZONES.some(tz => tz.value === safeValue);
  const options   = isInList ? TIMEZONES : [{ value: safeValue, label: safeValue || 'Unknown' }, ...TIMEZONES];

  return (
    <div className="form-group">
      <FieldLabel htmlFor={selectId} label={label} required={required} />
      <div className="fi-tz-wrap">
        <select
          id={selectId}
          name={name}
          value={safeValue}
          onChange={onChange}
          required={required}
          className="fi-tz-select"
          {...rest}
        >
          {options.map(tz => (
            <option key={tz.value} value={tz.value}>{tz.label}</option>
          ))}
        </select>
        <span className="fi-tz-arrow" aria-hidden="true" />
      </div>
      {detecting && (
        <small className="fi-tz-detecting">Detecting timezone from venue location...</small>
      )}
      {!detecting && autoDetectNote && (
        <small className="fi-tz-autonote">{autoDetectNote}</small>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// TextInput — single-line free text field
//
// Deliberately a thin wrapper: it renders a bare <input> inside
// .form-group, which already gets its border/padding/focus-ring
// styling for free from the global ".form-group input" rule in
// index.css (section 7 — FORMS). No new CSS needed; this is what
// makes it "match the rest of the app" instead of introducing a
// parallel style.
// ─────────────────────────────────────────────────────────────
export function TextInput({
  label = '',
  value = '',
  onChange,
  required = false,
  hint,
  id,
  name,
  placeholder,
  type = 'text',
  ...rest
}) {
  const inputId = id || name || (label ? `text-input-${label.replace(/\s+/g, '-').toLowerCase()}` : 'text-input');

  return (
    <div className="form-group">
      <FieldLabel htmlFor={inputId} label={label} required={required} />
      <input
        type={type}
        id={inputId}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        {...rest}
      />
      {hint && <small className="fi-hint">{hint}</small>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// TextArea — multi-line free text field
//
// Same principle as TextInput: bare <textarea> inside .form-group
// picks up the shared styling (including the app's resize:vertical
// / min-height:100px textarea rule) automatically.
// ─────────────────────────────────────────────────────────────
export function TextArea({
  label = '',
  value = '',
  onChange,
  required = false,
  hint,
  id,
  name,
  placeholder,
  rows = 3,
  ...rest
}) {
  const inputId = id || name || (label ? `textarea-${label.replace(/\s+/g, '-').toLowerCase()}` : 'textarea');

  return (
    <div className="form-group">
      <FieldLabel htmlFor={inputId} label={label} required={required} />
      <textarea
        id={inputId}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        rows={rows}
        required={required}
        {...rest}
      />
      {hint && <small className="fi-hint">{hint}</small>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ColorInput — original Coloris-powered picker
//
// EXCEPTION TO THE NO-INLINE-STYLE RULE: `background` on the swatch
// preview and `color` on the hex text are literally the arbitrary
// hex color the person picked. That's user data, not a themeable
// design token, so it cannot be expressed as a static CSS class —
// there is no finite set of classes that could cover every possible
// hex value. Everything else (layout, borders, font, focus ring) is
// a real class; only the two truly-dynamic color values stay inline,
// and the old onFocus/onBlur JS handlers that toggled box-shadow are
// gone in favor of a plain CSS :focus rule.
// ─────────────────────────────────────────────────────────────

let colorisInitialized = false;

const DPP_SWATCHES = [
  '#ff69b4','#c557c5','#65025e',
  '#ff0000','#ff6600','#ffcc00',
  '#00cc44','#0088ff','#cc00ff',
  '#ffffff','#888888','#000000',
];

export function ColorInput({ label = 'Color', value = '#ff69b4', onChange, required = false, ...rest }) {
  const inputId  = useRef(`coloris-${Math.random().toString(36).slice(2, 8)}`).current;
  const inputRef = useRef(null);
  const [colorisAvailable, setColorisAvailable] = useState(false);

  useEffect(() => {
    if (!colorisInitialized && window.Coloris) {
      window.Coloris({
        themeMode: 'dark',
        alpha:     false,
        format:    'hex',
        swatches:  DPP_SWATCHES,
      });
      colorisInitialized = true;
    }
    setColorisAvailable(!!window.Coloris);
  }, []);

  useEffect(() => {
    const el = inputRef.current;
    if (!el || !colorisAvailable) return;
    const handleNativeInput = (e) => {
      const hex = e.target.value;
      if (/^#[0-9a-fA-F]{6}$/.test(hex) && typeof onChange === 'function') onChange(hex.toLowerCase());
    };
    el.addEventListener('input', handleNativeInput);
    return () => el.removeEventListener('input', handleNativeInput);
  }, [colorisAvailable, onChange]);

  if (!colorisAvailable) {
    return (
      <div className="form-group" {...rest}>
        <FieldLabel htmlFor={inputId} label={label} required={required} />
        <div className="fi-color-row">
          <input
            id={inputId}
            type="color"
            value={value}
            onChange={(e) => typeof onChange === 'function' && onChange(e.target.value)}
            required={required}
            className="fi-color-swatch-input"
          />
          <span className="fi-color-hex-label">{value}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="form-group" {...rest}>
      <FieldLabel htmlFor={inputId} label={label} required={required} />
      <div className="fi-color-row">
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          data-coloris
          value={value}
          onChange={() => {}}
          onKeyDown={(e) => e.preventDefault()}
          required={required}
          className="fi-coloris-input"
          /* Dynamic exception — see file header comment */
          style={{ background: value, color: isLightColor(value) ? '#000000' : '#ffffff' }}
        />
        <span className="fi-color-hex-caption">{value}</span>
      </div>
    </div>
  );
}

function isLightColor(hex) {
  const clean = (hex || '').replace('#', '');
  if (clean.length !== 6) return false;
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return (r * 0.299 + g * 0.587 + b * 0.114) > 150;
}